/**
 * The error every API call rejects with.
 *
 * It extends `Error` and keeps `message` set to whatever the server put in
 * `json.error` - exactly what the old per-file `throw new Error(json.error ??
 * 'Request failed')` produced. That is load-bearing: roughly a dozen components
 * render `err instanceof Error ? err.message : ...`, and the web UI must not
 * change wording. Everything structured is additive.
 *
 * `kind` exists because mobile cannot render a useful screen from a string.
 * "You're offline" and "your session expired" need different treatment, and
 * string-matching an error message to tell them apart is how that goes wrong.
 */

export type ApiErrorKind =
  /** The request never reached the server - no connectivity, DNS failure, CORS. */
  | 'offline'
  /** The caller aborted, or the request exceeded its timeout. */
  | 'aborted'
  /** 401. The session is missing, expired, or was rejected. */
  | 'unauthorized'
  /** 403. Authenticated but not permitted - typically a plan gate. */
  | 'forbidden'
  /** 404. */
  | 'notFound'
  /** Any other 4xx. The request was wrong; retrying unchanged will not help. */
  | 'client'
  /** Any 5xx. The request may succeed if retried. */
  | 'server'
  /** A 2xx whose body was not the JSON envelope we expect. */
  | 'malformed';

export interface ApiErrorInit {
  kind: ApiErrorKind;
  message: string;
  status?: number | null;
  url?: string;
  body?: unknown;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** HTTP status, or null when the request never got a response. */
  readonly status: number | null;
  /** Absolute request URL, for logging. */
  readonly url: string;
  /** Parsed response body when there was one, for callers needing detail. */
  readonly body: unknown;
  /**
   * The underlying failure, for network errors. A field rather than the
   * `Error` `cause` option, which needs the ES2022 lib the web app does not
   * target and Hermes does not reliably implement.
   */
  readonly cause: unknown;

  constructor({ kind, message, status = null, url = '', body = null, cause = null }: ApiErrorInit) {
    super(message);
    this.name = 'ApiError';
    this.cause = cause;
    this.kind = kind;
    this.status = status;
    this.url = url;
    this.body = body;

    // Required when targeting ES5-ish output, which the RN/Hermes toolchain
    // still does: without it `instanceof ApiError` is false in the mobile app.
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Retrying unchanged could plausibly succeed.
   *
   * 429 is a `client` kind by status mapping but is the one 4xx where retrying
   * the identical request is not just allowed but expected - the mobile grading
   * outbox leans on this to survive a rate-limit burst without dropping a
   * decision the user has already made.
   */
  get isRetryable(): boolean {
    return this.kind === 'offline' || this.kind === 'server' || this.status === 429;
  }

  /** The session needs re-establishing before anything else will work. */
  get isAuthFailure(): boolean {
    return this.kind === 'unauthorized';
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** Map an HTTP status onto the error taxonomy. */
export function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status >= 500) return 'server';
  return 'client';
}
